import usePreviewDocumentTemplate from '@/api/mutations/documentTemplate/usePreviewDocumentTemplate';
import DocumentTemplatePreview from '@/ui/components/documentTemplatePreview/DocumentTemplatePreview';
import {
  Alert,
  Card,
  DividerWithLabel,
  Field,
  FileDropZone,
  InputGroup,
} from '@payaca/ambrosia';

import { FC, useState } from 'react';

export type TProps = {
  isMobile?: boolean;
  name: string;
  value?: {
    raw: string;
    preview: string;
  } | null;
  onChange: (value?: {
    raw: string;
    preview: string;
    usedVariables: string[];
  }) => void;
  /**
   * 'full' (default) shows the rendered preview once a value is set.
   * 'entryOnly' always shows the upload / paste controls and never the
   * preview — for callers that render the preview themselves.
   */
  variant?: 'full' | 'entryOnly';
};

const UploadTemplateField: FC<TProps> = (props) => {
  const { isMobile, name, value, onChange, variant = 'full' } = props;

  const [isReadingFileLocally, setIsReadingFileLocally] = useState(false);
  const [rawHTMLText, setRawHTMLText] = useState<string>();

  const {
    mutateAsync: generateDocumentTemplatePreview,
    error,
    isPending: isGeneratingPreview,
  } = usePreviewDocumentTemplate();

  const handlePreviewGeneration = async (rawTemplate: string) => {
    const preview = await generateDocumentTemplatePreview({
      templateHtml: rawTemplate,
    });

    if (
      preview.previewDocumentTemplate.__typename ===
      'DocumentTemplatePreviewSuccess'
    ) {
      onChange({
        preview: preview.previewDocumentTemplate.preview,
        raw: rawTemplate,
        usedVariables: preview.previewDocumentTemplate.usedVariables,
      });
    }
  };

  const onFileUpload = async (file: File) => {
    setIsReadingFileLocally(true);

    try {
      const rawTemplate = await file.text();

      await handlePreviewGeneration(rawTemplate);
    } finally {
      setIsReadingFileLocally(false);
    }
  };

  const onRawHTMLPreviewClick = async () => {
    if (rawHTMLText) {
      await handlePreviewGeneration(rawHTMLText);

      setRawHTMLText(undefined);
    }
  };

  return (
    <Field name={name} className="flex flex-col gap-4">
      {error && (
        <Alert colour="red">
          <p>Error previewing the template</p>
          <ul>
            <li>{error.message}</li>
          </ul>
        </Alert>
      )}

      {value && variant === 'full' ? (
        <DocumentTemplatePreview html={value.preview} isMobile={isMobile} />
      ) : (
        <Card className="w-full">
          <Card.Body>
            <FileDropZone
              isLoading={isReadingFileLocally || isGeneratingPreview}
              accepts={{ extensions: ['text/html'] }}
              dropzoneOptions={{
                maxFiles: 1,
                multiple: false,
                onDrop: (files) => {
                  void onFileUpload(files[0]);
                },
              }}
            />

            <DividerWithLabel className="my-4" label="or" />

            <Field.Label className="mb-2">Paste HTML code</Field.Label>
            <InputGroup>
              <InputGroup.Input value={rawHTMLText} onChange={setRawHTMLText} />
              <InputGroup.Button
                variant="secondary"
                onClick={onRawHTMLPreviewClick}
              >
                Preview
              </InputGroup.Button>
            </InputGroup>
          </Card.Body>
        </Card>
      )}
    </Field>
  );
};

export default UploadTemplateField;
